import {
    ReadingStatuses,
    ReadingTypesV1,
    ReadingUnits,
    VesselProtocolInfoV1LevelColorEnum,
    type ReadingsV1,
    type ReadingV1,
    type VesselProtocolInfoV1
} from '@geckoal/gecko-api-client'
import { RfStrength } from '@geckoal/web-models'

export type HealthCheckSeverity = 'ok' | 'warn' | 'danger' | 'unknown'

/** A single row in the Health Checks card: what we expect vs. what we currently see. */
export interface HealthCheck {
    key: string
    label: string
    /** The value we expect to see, e.g. a target range or a mode name. */
    expected: string
    /** The value the vessel is currently reporting. */
    current: string
    severity: HealthCheckSeverity
    /** Optional extra explanation shown as a tooltip on the row. */
    tooltip?: string
    /**
     * Marks a fault that shouldn't happen at all — the vessel is misbehaving rather
     * than simply drifting out of range. Rendered with a highlighted, pulsing row.
     */
    alert?: boolean
}

/**
 * Expected vs. actual pairs derived from the live spa state (MQTT).
 * Produced by SpaStateValidator, which already owns the spa connection,
 * so the Health Checks card does not need a second socket.
 */
export interface SpaHealthCheck {
    expected?: string
    current?: string
    /**
     * Set when expected and current disagree. Holds the full explanation of why the
     * state is wrong, and marks the row as a fault needing attention.
     */
    issue?: string
    /** Informational note, shown as the row tooltip when there is no issue. */
    note?: string
    /**
     * Explicit severity for graded checks such as signal strength, where the value
     * sits on a scale rather than being simply right or wrong. When set, the row is
     * coloured accordingly but is not flagged as a hard fault.
     */
    severity?: HealthCheckSeverity
}

export interface SpaHealthChecks {
    connected: boolean
    connecting: boolean
    /** Populated when the spa connection failed, so the card can explain the gap. */
    error?: string
    operationMode?: SpaHealthCheck
    ecoSchedule?: SpaHealthCheck
    filtration?: SpaHealthCheck
    heaterSetPoint?: SpaHealthCheck
    rfSignal?: SpaHealthCheck
    moduleOnline?: SpaHealthCheck
}

/**
 * Human-readable RF link strength, matching the wording on the Gecko Spa Status page
 * so both views describe the same signal the same way.
 */
export function rfStrengthLabel(strength?: RfStrength): string {
    switch (strength) {
        case RfStrength.POOR:
            return 'Poor'
        case RfStrength.WEAK:
            return 'Weak'
        case RfStrength.FAIR:
            return 'Fair'
        case RfStrength.GOOD:
            return 'Good'
        case RfStrength.EXCELLENT:
            return 'Excellent'
        default:
            return 'Unknown'
    }
}

/** Mirrors the thresholds Gecko Spa Status uses to colour the signal chip. */
export function rfStrengthSeverity(strength?: RfStrength): HealthCheckSeverity {
    if (strength === undefined) return 'unknown'
    if (strength <= RfStrength.WEAK) return 'danger'
    if (strength === RfStrength.FAIR) return 'warn'
    return 'ok'
}

/**
 * Reading types shown in the Health Checks card, in display order.
 * Diagnostic readings (cpu temp, battery, wifi) are handled separately.
 */
export const HEALTH_CHECK_READING_ORDER: ReadingTypesV1[] = [
    ReadingTypesV1.FreeChlorine,
    ReadingTypesV1.TotalChlorine,
    ReadingTypesV1.Bromine,
    ReadingTypesV1.Ph,
    ReadingTypesV1.TotalAlkalinity,
    ReadingTypesV1.TotalHardness,
    ReadingTypesV1.CalciumHardness,
    ReadingTypesV1.CyanuricAcid,
    ReadingTypesV1.Salt,
    ReadingTypesV1.Orp,
    ReadingTypesV1.WaterTemp
]

export function severityForReadingStatus(status: ReadingStatuses): HealthCheckSeverity {
    switch (status) {
        case ReadingStatuses.Ok:
            return 'ok'
        case ReadingStatuses.Low:
        case ReadingStatuses.High:
            return 'warn'
        case ReadingStatuses.ReallyLow:
        case ReadingStatuses.ReallyHigh:
            return 'danger'
        default:
            return 'unknown'
    }
}

export function readingUnitSuffix(unit?: ReadingUnits): string {
    switch (unit) {
        case ReadingUnits.C:
            return '°C'
        case ReadingUnits.F:
            return '°F'
        case ReadingUnits.Ppm:
            return 'ppm'
        case ReadingUnits.MV:
            return 'mV'
        case ReadingUnits.Db:
            return 'dB'
        case ReadingUnits.Percent:
            return '%'
        default:
            return ''
    }
}

export function formatReadingValue(reading: ReadingV1): string {
    const rounded = Math.round(reading.value * 100) / 100
    const suffix = readingUnitSuffix(reading.unit)
    return suffix ? `${rounded} ${suffix}` : `${rounded}`
}

/**
 * Merges the reading buckets returned by the vessel status into a single
 * "what the vessel currently believes" map. Monitor readings win over water
 * report readings, which win over computed readings, matching the precedence
 * the chem engine uses when it picks a value to act on.
 */
export function mergeCurrentReadings(status?: {
    readings?: ReadingsV1
    monitorReadings?: ReadingsV1
    reportReadings?: ReadingsV1
    computedReadings?: ReadingsV1
}): ReadingsV1 {
    if (status === undefined) return {}
    if (status.readings !== undefined && Object.keys(status.readings).length > 0) return status.readings

    return {
        ...(status.computedReadings ?? {}),
        ...(status.reportReadings ?? {}),
        ...(status.monitorReadings ?? {})
    }
}

/**
 * The protocol exposes its thresholds as pre-formatted, colour-coded strings.
 * The blue band is the "in range" band, so it is what we expect to see.
 */
export function expectedRangeForReadingType(readingType: ReadingTypesV1, protocolInfo?: VesselProtocolInfoV1): string | undefined {
    const config = protocolInfo?.readingConfigs?.find((c) => c.readingType === readingType)
    if (config === undefined) return undefined

    const okLevel = config.levels?.find((level) => level.color === VesselProtocolInfoV1LevelColorEnum.Blue)
    return okLevel?.thresholdDescription
}

/** Short, glanceable duration: "42s", "7 min", "3 h", "12 d". */
export function compactDuration(from: Date, now: Date = new Date()): string {
    const seconds = Math.max(0, Math.round((now.getTime() - from.getTime()) / 1000))
    if (seconds < 60) return `${seconds}s`

    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes} min`

    const hours = Math.floor(minutes / 60)
    if (hours < 48) return `${hours} h`

    return `${Math.floor(hours / 24)} d`
}

/** Builds a check from a spa state pair, flagging it when actual doesn't match expected. */
export function checkFromSpaPair(key: string, label: string, pair: SpaHealthCheck | undefined, spaHealth: SpaHealthChecks | undefined): HealthCheck {
    const expected = pair?.expected ?? '—'

    // The spa connection is opened by SpaStateValidator and takes a moment, so
    // distinguish "still connecting" from "connected but nothing reported".
    if (spaHealth === undefined || spaHealth.connecting) {
        return { key, label, expected, current: 'Connecting…', severity: 'unknown', tooltip: 'Still connecting to the spa to read its live state.' }
    }

    if (!spaHealth.connected) {
        return {
            key,
            label,
            expected,
            current: 'Not connected',
            severity: 'unknown',
            tooltip:
                spaHealth.error !== undefined
                    ? `Could not connect to the spa to validate its state: ${spaHealth.error}`
                    : 'Not connected to the spa, so its live state could not be read.'
        }
    }

    if (pair?.current === undefined) {
        return { key, label, expected, current: 'Unknown', severity: 'unknown', tooltip: pair?.note }
    }

    // An issue means the spa is in a state it should never be in, not just a value
    // drifting out of range — call it out loudly.
    if (pair.issue !== undefined) {
        return { key, label, expected, current: pair.current, severity: 'danger', alert: true, tooltip: pair.issue }
    }

    // Graded checks carry their own severity and are never hard faults.
    if (pair.severity !== undefined) {
        return { key, label, expected, current: pair.current, severity: pair.severity, tooltip: pair.note }
    }

    if (pair.expected === undefined) {
        return { key, label, expected: 'User controlled', current: pair.current, severity: 'unknown', tooltip: pair.note }
    }

    return { key, label, expected: pair.expected, current: pair.current, severity: 'ok' }
}
